// Path: frontend/src/pages/admin/components/chat-records/PlanChatRecordsTab.tsx

import React, { useState, useEffect } from 'react';
import {
  getUserPlanChatRecords,
  type UserPlanChatRecord,
  type LessonPlanChatRecord,
  type PlanMessage
} from '../../../../lib/apis_chatmsg';
import ChatRecordsFilter, { type FilterData, type FilterValues } from './ChatRecordsFilter';
import ChatRecordsResult, { type BaseLessonRecord } from './ChatRecordsResult';
import PlanMessagesTable from './PlanMessagesTable';

const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PlanChatRecordsTab: React.FC = () => {
  const [filterValues, setFilterValues] = useState<FilterValues>({
    userId: '',
    courseId: '',
    stageId: '',
    lessonId: '',
    startDate: getDateDaysAgo(7),
    endDate: getTodayDate()
  });

  const [chatRecords, setChatRecords] = useState<UserPlanChatRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const filterData: FilterData = {
    users: [],
    courses: [],
    stages: [],
    lessons: []
  };

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = async () => {
    setIsLoading(true);
    setHasSearched(true);
    try {
      const params: Record<string, any> = {};
      if (filterValues.startDate) params.start_date = filterValues.startDate;
      if (filterValues.endDate) params.end_date = filterValues.endDate;

      const data = await getUserPlanChatRecords(params);
      setChatRecords(data);
    } catch (error) {
      console.error('Failed to fetch plan chat records:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFilterValues({
      userId: '',
      courseId: '',
      stageId: '',
      lessonId: '',
      startDate: getDateDaysAgo(7),
      endDate: getTodayDate()
    });
    setChatRecords([]);
    setHasSearched(false);
  };

  const handleValueChange = (key: keyof FilterValues, value: string | number | '') => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  };

  const countMessages = (user: UserPlanChatRecord): number => {
    return user.lessons.reduce((total, lesson) => {
      return total + lesson.plans.reduce((pTotal, plan) => {
        return pTotal + plan.messages.length;
      }, 0);
    }, 0);
  };

  const totalMessages = chatRecords.reduce((sum, user) => sum + countMessages(user), 0);

  const getLessons = (user: UserPlanChatRecord): BaseLessonRecord[] => {
    return user.lessons;
  };

  const renderLessonMessages = (lesson: BaseLessonRecord, lessonKey: string): React.ReactNode => {
    const fullLesson = chatRecords
      .flatMap(u => u.lessons)
      .find(l => l.lesson_id === lesson.lesson_id);
    
    if (!fullLesson) return null;

    const allMessages: (PlanMessage & { plan_id: number; version_number: string })[] = 
      (fullLesson as LessonPlanChatRecord).plans.flatMap(plan =>
        plan.messages.map(msg => ({
          ...msg,
          plan_id: plan.plan_id,
          version_number: plan.version_number
        }))
      );

    return <PlanMessagesTable messages={allMessages} />;
  };

  return (
    <>
      <ChatRecordsFilter
        filterData={filterData}
        values={filterValues}
        isLoading={isLoading}
        onValueChange={handleValueChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      <ChatRecordsResult
        records={chatRecords}
        isLoading={isLoading}
        hasSearched={hasSearched}
        totalMessages={totalMessages}
        countMessages={countMessages}
        getLessons={getLessons}
        renderLessonMessages={renderLessonMessages}
      />
    </>
  );
};

export default PlanChatRecordsTab;
